/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Panel } from "../../../../ui/components/Panel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { useTreeState } from "../../../../ui/components/Tree/useTreeState";
import { ITreeViewProps, TreeView } from "../../../../ui/components/Tree/TreeView";
import {
	ITreeNode,
	ITreeNodeAction,
	TREE_NODE_ACTION_STYLE,
	TREE_VIEW_STYLE
} from "../../../../ui/components/Tree/TreeViewTypes";
import { BlueprintActionNode_v2, NODE_TYPES, TBlueprintActionSchema } from "@adapptio/lib-core";
import {
	ISchemaBuilderAny,
	ISchemaFlowNodeTypeDefinition,
	ISchemaIntegrationRefModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericBlueprintSchema,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ActionEditorView } from "../ActionEditorView";
import { createTreeNodesFromMapModel } from "../../../../ui/components/Tree/utils";
import { ACTION_BUTTON_STYLE } from "../../../../ui/components/ActionButton";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { TOOLBAR_BUTTON_SIZE, ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ActionEditSelection } from "../../Common/ActionEditSelection";
import { useContextMenu } from "../../../../ui/hooks/useContextMenu";

interface IGetTreeOpts {
	actions?: ITreeNodeAction[];
}

function getNodeTree(
	t: TTranslateHookFn,
	model: TGetBlueprintSchemaModel<TBlueprintActionSchema>,
	view: ActionEditorView
): ITreeNode[] {
	const nodeList = model.props.spec.props.nodes.value;

	const items = nodeList.items.map((item) => {
		const itemType = item.schema.opts.nodeTypes[
			item.type
		] as ISchemaFlowNodeTypeDefinition<TGenericBlueprintSchema>;

		let typeLabel: string;
		let elementLabel: string;
		let icon: string;

		const varName = item.varName.value && item.varName.value != "" ? item.varName.value : undefined;
		const comment = item.comment.value && item.comment.value != "" ? item.comment.value.split("\n")[0] : null;

		if (item.type === "integration") {
			const opts = item.opts.template as ISchemaIntegrationRefModel;
			const int = view.getIntegrationType(opts.integrationType);
			const fn = view.getIntegrationFunction(opts.integrationType, opts.functionName);
			const integrationResource = view.getResourceService().getResourceById(opts.integrationId);

			icon = fn.icon ?? int.icon ?? "mdi/help-circle";

			typeLabel = int && fn
				? ((integrationResource?.descriptor.name ? integrationResource?.descriptor.name : t("editor", int.label))
					+ " / " + t("editor", fn.label))
				: t("editor", itemType.label);
		} else if (item.type === NODE_TYPES.ACTION_V2) {
			const actionOpts = item.opts.template as TGetBlueprintSchemaModel<typeof BlueprintActionNode_v2.opts>;
			const resourceDesc = actionOpts.constant && actionOpts.constant.props.action.actionId.constant
				? view.getRelatedResourceDescrption(actionOpts.constant.props.action.actionId.constant.value)
				: undefined;

			icon = itemType.icon;
			typeLabel = t("editor", itemType.label);
			elementLabel = resourceDesc?.name;
		} else {
			icon = itemType.icon;
			typeLabel = t("editor", itemType.label);
		}

		const label = varName ?? comment ?? elementLabel ?? typeLabel;
		const description = label !== typeLabel ? typeLabel : undefined;

		return {
			id: String(item.nodeId),
			label: label,
			description: description,
			icon: icon,
			children: null,
			isLoadable: false,
			selectable: true,
			metaData: {
				nodeList: nodeList,
				modelNode: item
			}
		};
	});

	const rootClass = BEM("edt-module-action-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Nodes",
			icon: "mdi/hubspot",
			defaultExpanded: true,
			children: items.length > 0 ? items : null,
			className: rootClass.toClassName()
		}
	];
}

function getParametersTree(
	model: TGetBlueprintSchemaModel<TBlueprintActionSchema>,
	{ actions = [] }: IGetTreeOpts = {}
): ITreeNode[] {
	const viewProps = model.props.spec.props;
	const paramsModel = viewProps.params;
	const mapModel = paramsModel.props.props.props;

	const items: ITreeNode[] = createTreeNodesFromMapModel(mapModel, { icon: "mdi/record-circle-outline" });

	const rootClass = BEM("edt-module-action-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Parameters",
			icon: "mdi/plus-circle",
			defaultExpanded: true,
			children: items.length > 0 ? items : [],
			className: rootClass.toClassName(),
			actions
		}
	];
}

function createAddMenu(
	model: TGetBlueprintSchemaModel<TBlueprintActionSchema>,
	selection: ActionEditSelection
): IDropdownMenuItem[] {
	const viewProps = model.props.spec.props;
	const paramsModel = viewProps.params;
	const paramsMapModel = paramsModel.props.props.props;

	const addParameter = () => {
		const parameter = paramsMapModel.schema.addElement(
			paramsMapModel,
			paramsMapModel.schema.getUniqueKey(paramsMapModel, "parameter", true),
			undefined,
			true
		);

		selection.selectParameter(paramsMapModel, parameter);
	};

	return [
		{
			id: "parameter",
			label: "Add Parameter",
			icon: "mdi/record-circle-outline",
			onClick: addParameter
		}
	];
}

/*
 * Toolbar
 */
export interface IActionEditorOutlineToolbarProps {
	view: ActionEditorView;
}

export const ActionEditorOutlineToolbar: FC<IActionEditorOutlineToolbarProps> = ({ view }) => {
	const actionModel = view.getActionModel();

	const [ modelRef, setModelRef ] = useState(() => ({
		model: actionModel.getModel()
	}));

	const addMenuItems = useMemo(
		() => createAddMenu(modelRef?.model, actionModel.getSelection()),
		[ modelRef ]
	);

	useEffect(() => {
		const handleUpdate = () =>
			setModelRef({
				model: actionModel.getModel()
			});

		onEvent(actionModel.onModelChange, handleUpdate);

		return () => {
			offEvent(actionModel.onModelChange, handleUpdate);
		};
	}, [ actionModel ]);

	// @todo translate
	return (
		<>
			<ToolbarTitle>Outline</ToolbarTitle>
			<ToolbarSpacer grow={1} />
			<ToolbarButton
				icon="mdi/plus"
				size={TOOLBAR_BUTTON_SIZE.SMALL}
				menuItems={addMenuItems}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				style={ACTION_BUTTON_STYLE.PRIMARY}
			/>
		</>
	);
};

/*
 * Content
 */
export interface IActionEditorOutlineContentProps {
	view: ActionEditorView;
}

export const ActionEditorOutlineContent: FC<IActionEditorOutlineContentProps> = ({ view }) => {
	const t = useTranslate();
	const actionModel = view.getActionModel();

	const baseClass = BEM("edt-module-action-editor-outline");

	const [ modelRef, setModelRef ] = useState(() => ({
		model: actionModel.getModel()
	}));

	const [ nodeTreeState, handleNodeTreeStateUpdate ] = useTreeState({
		nodes: { root: { expanded: true } }
	});
	const [ paramsTreeState, handleParamsTreeStateUpdate ] = useTreeState({
		nodes: { root: { expanded: true } }
	});
	const [ selection, setSelection ] = useState([]);

	const { showMenu } = useContextMenu("outlineNode");

	const handleContextMenuClick = useCallback<ITreeViewProps["onNodeContextMenu"]>(
		(treeNode, event) => {
			showMenu(event, { treeNode });
		},
		[ showMenu ]
	);

	// Tree items
	const nodeTreeItems = useMemo(() => {
		return getNodeTree(t, modelRef.model, view);
	}, [ modelRef, view ]);

	// Nodes
	const handleNodeSelect = useCallback((node: ITreeNode) => {
		const modelNode = node.metaData?.modelNode;
		const nodeList = node.metaData?.nodeList;

		if (isSchemaFlowNodeModel(modelNode) && isSchemaFlowNodeListModel(nodeList)) {
			actionModel.getSelection().selectFlowNode(nodeList, modelNode);
		}
	}, []);

	// Parameters
	const handleParamSelect = useCallback(
		(node: ITreeNode) => {
			const actionModel = view.getActionModel();
			const mapModel = node.metaData?.mapModel;
			const mapEntry = node.metaData?.mapEntry;

			if (isSchemaConstMapModel<ISchemaBuilderAny>(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
				actionModel.getSelection().selectParameter(mapModel, mapEntry);
			}
		},
		[ view ]
	);

	const addParameter = useCallback(() => {
		const actionModel = view.getActionModel();
		const model = actionModel.getModel();
		const viewProps = model.props.spec.props;
		const paramsModel = viewProps.params;
		const paramsMapModel = paramsModel.props.props.props;

		const parameter = paramsMapModel.schema.addElement(
			paramsMapModel,
			paramsMapModel.schema.getUniqueKey(paramsMapModel, "parameter", true),
			undefined,
			true
		);

		actionModel.getSelection().selectParameter(paramsMapModel, parameter);
	}, [ view ]);

	const paramTreeActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-parameter",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Parameter",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				hasPrimaryAction: true,
				onClick: addParameter
			}
		],
		[ addParameter ]
	);

	const paramsTreeItems = useMemo(() => {
		return getParametersTree(modelRef.model, { actions: paramTreeActions });
	}, [ modelRef, paramTreeActions, actionModel ]);

	const handleNodeDragDrop = useCallback<ITreeViewProps["onNodeDragDrop"]>(
		({
			draggedNode,
			dropTargetNode
		}) => {
			const mapModel = draggedNode.metaData?.mapModel;
			const mapEntry = draggedNode.metaData?.mapEntry;

			if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
				const currentIndex = mapModel.items.findIndex(
					(item) => item === mapEntry
				);

				const targetNode = dropTargetNode.metaData?.mapEntry;
				const targetNodeIndex = targetNode && isSchemaConstMapModelEntry(targetNode)
					? mapModel.items.findIndex((item) => item === targetNode)
					: null;

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				mapModel.schema.moveElement(
					mapModel,
					currentIndex,
					newIndex,
					true
				);

				return;
			}
		},
		[ paramsTreeItems ]
	);

	useEffect(() => {
		const handleUpdate = () =>
			setModelRef({
				model: actionModel.getModel()
			});

		const handleSelectionChange = () =>
			setSelection(
				actionModel
					.getSelection()
					.getItems()
					.map((item) => String(item.nodeId))
			);

		onEvent(actionModel.onModelChange, handleUpdate);
		onEvent(actionModel.onRender, handleUpdate);
		onEvent(actionModel.getSelection().onChange, handleSelectionChange);

		return () => {
			offEvent(actionModel.onModelChange, handleUpdate);
			offEvent(actionModel.onRender, handleUpdate);
			offEvent(actionModel.getSelection().onChange, handleSelectionChange);
		};
	}, [ actionModel ]);

	return (
		<Panel scroll={true} className={baseClass.toClassName()}>
			<div className={baseClass.element("nodes").toClassName()}>
				<TreeView
					items={nodeTreeItems}
					state={nodeTreeState}
					selections={selection}
					onNodeSelect={handleNodeSelect}
					onStateUpdate={handleNodeTreeStateUpdate}
					style={TREE_VIEW_STYLE.DEFAULT}
					compact={false}
				/>
				{!nodeTreeItems[0].children ? (
					<div className={baseClass.element("note").toClassName()}>
						Add nodes by dragging them from the right panel.
					</div>
				) : null}
			</div>
			<div className={baseClass.element("parameters").toClassName()}>
				<TreeView
					items={paramsTreeItems}
					state={paramsTreeState}
					selections={selection}
					onNodeSelect={handleParamSelect}
					onStateUpdate={handleParamsTreeStateUpdate}
					style={TREE_VIEW_STYLE.DEFAULT}
					onNodeContextMenu={handleContextMenuClick}
					isDraggable
					onNodeDragDrop={handleNodeDragDrop}
				/>
			</div>
		</Panel>
	);
};
